import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import {
    colors as dsColors,
    tokens as dsTokens,
    tokensToCssVarMap,
    tokensToMuiPalette,
    tokensToMuiTypography,
} from '../design-system/tokens';

// MUI expects numeric pixel values for shape.borderRadius and the
// individual component overrides. Our tokens are CSS strings (e.g.
// "12px"); strip the unit so the MUI surfaces match the design-system
// radius scale without a hardcoded literal.
const px = (value) => {
    const parsed = parseFloat(value);
    if (!Number.isFinite(parsed)) {
        throw new Error(`ThemeContext: cannot derive pixel value from "${value}"`);
    }
    return parsed;
};

const MUI_RADIUS = {
    button: px(dsTokens.radius.xl),   // 12px — matches the previous literal
    surface: px(dsTokens.radius['2xl']), // 16px — matches the previous literal
};

// Create Theme Context
const ThemeContext = createContext();

// Custom hook to use theme. Preserved unchanged so the migration of
// individual components is a token swap, not an API shift.
export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

// Build a MUI theme for a mode. The palette + typography come from
// tokens.ts so MUI dialogs and Tailwind utilities can't disagree on
// surface/border/text colors.
const buildMuiTheme = (mode) =>
    createTheme({
        palette: tokensToMuiPalette(mode),
        typography: tokensToMuiTypography(),
        shape: {
            borderRadius: MUI_RADIUS.button,
        },
        components: {
            MuiButton: {
                styleOverrides: {
                    root: {
                        borderRadius: MUI_RADIUS.button,
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        borderRadius: MUI_RADIUS.surface,
                        backgroundImage: 'none',
                    },
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        borderRadius: MUI_RADIUS.surface,
                        backgroundImage: 'none',
                    },
                },
            },
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none',
                    },
                },
            },
        },
    });

const LIGHT_THEME = buildMuiTheme('light');
const DARK_THEME = buildMuiTheme('dark');

const STYLE_TAG_ID = 'ds-tokens';

const renderCssVarBlock = (selector, map) => {
    const body = Object.entries(map)
        .map(([key, value]) => `  ${key}: ${value};`)
        .join('\n');
    return `${selector} {\n${body}\n}`;
};

const buildTokenStyleSheet = () => {
    const lightVars = tokensToCssVarMap('light');
    const darkVars = tokensToCssVarMap('dark');
    return [
        renderCssVarBlock(':root', lightVars),
        renderCssVarBlock('.dark', darkVars),
    ].join('\n\n');
};

// Theme Provider Component
export const ThemeProvider = ({ children }) => {
    const [darkMode, setDarkMode] = useState(() => {
        if (typeof window === 'undefined') return true;
        const savedTheme = localStorage.getItem('radix-theme-mode');
        return (
            savedTheme === 'dark' ||
            (!savedTheme && window.matchMedia('(prefers-color-scheme: dark)').matches)
        );
    });

    const muiTheme = darkMode ? DARK_THEME : LIGHT_THEME;

    const toggleDarkMode = () => {
        setDarkMode((prev) => !prev);
    };

    // Install the CSS-var stylesheet once. The :root block holds light
    // values; the .dark block overrides them when the documentElement
    // class flips below. This keeps Tailwind utility classes
    // (`bg-surface-panel`, `border-border-subtle`, ...) mode-aware without
    // any per-component branching.
    useEffect(() => {
        if (typeof document === 'undefined') return;
        let tag = document.getElementById(STYLE_TAG_ID);
        if (!tag) {
            tag = document.createElement('style');
            tag.id = STYLE_TAG_ID;
            document.head.appendChild(tag);
        }
        tag.textContent = buildTokenStyleSheet();
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        localStorage.setItem('radix-theme-mode', darkMode ? 'dark' : 'light');

        if (darkMode) {
            document.documentElement.classList.remove('light');
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
            document.documentElement.classList.add('light');
        }
    }, [darkMode]);

    const value = useMemo(
        () => ({
            darkMode,
            toggleDarkMode,
            theme: muiTheme,
            // Active-mode color tokens plus the mode-independent token
            // categories. Migrated components read colors from here
            // instead of hardcoding hexes; everything else still works
            // through the existing `darkMode` flag.
            mode: darkMode ? 'dark' : 'light',
            tokens: {
                colors: darkMode ? dsColors.dark : dsColors.light,
                typography: dsTokens.typography,
                spacing: dsTokens.spacing,
                radius: dsTokens.radius,
                borderWidth: dsTokens.borderWidth,
                elevation: dsTokens.elevation,
                focusRing: dsTokens.focusRing,
                motion: dsTokens.motion,
            },
        }),
        [darkMode, muiTheme]
    );

    return (
        <ThemeContext.Provider value={value}>
            <MuiThemeProvider theme={muiTheme}>
                <CssBaseline />
                {children}
            </MuiThemeProvider>
        </ThemeContext.Provider>
    );
};

export default ThemeProvider;
